/**
 * 发行人问卷列表页
 * 解决发行人无法查看和管理多个问卷的问题
 */
import React, { useState } from 'react';
import { tokenManager } from '../../utils/tokenManager';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  DatePicker,
  Tooltip,
  Dropdown,
  message,
  Row,
  Col,
  Statistic,
  Progress,
  Empty,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  SendOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { StandardPageLayout } from '../../components/layout';
import { useGetIssuerProjectsQuery } from '../../store/api/issuerProjectApi';
import {
  useGetQuestionnaireByIdQuery,
  useWithdrawQuestionnaireMutation,
} from '../../store/api/questionnaireApi';
import { QuestionnaireDetailModal } from '../../components/screening/QuestionnaireDetailModal';
import { UnifiedStatusTag, useUnifiedConfirm } from '../../components/unified';
import type { ColumnsType } from 'antd/es/table';
import type { InitialScreeningQuestionnaire } from '../../types/questionnaire';
import {
  formatProjectId,
  getFullProjectIdTooltip,
} from '../../utils/questionnaireEnumMapping';
import '../../styles/emerald-table.css';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

// 问卷状态类型
type QuestionnaireStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'need_supplement';

// 类型保护函数
const isValidQuestionnaireStatus = (
  status: string
): status is QuestionnaireStatus => {
  return [
    'draft',
    'submitted',
    'under_review',
    'approved',
    'rejected',
    'need_supplement',
  ].includes(status);
};

// 问卷数据接口
interface IssuerQuestionnaire {
  id: string; // 项目ID
  questionnaireId?: string; // 问卷ID（用于编辑和查询）
  projectName: string;
  status: QuestionnaireStatus;
  currentVersion: number;
  totalVersions: number;
  aiScore?: number;
  riskLevel?: 'low' | 'medium' | 'high';
  submittedAt?: string;
  reviewedAt?: string;
  reviewedBy?: string;
  lastActivity: string;
  rejectedCount: number;
  approvedCount: number;
  canEdit: boolean;
  canResubmit: boolean;
  canDelete: boolean;
  canWithdraw: boolean; // 新增：是否可撤回
}

// 不再使用模拟数据，只从后端API获取真实数据

const IssuerQuestionnaireList: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useUnifiedConfirm();
  const { t, i18n } = useTranslation();

  // 使用API获取发行人项目数据
  const {
    data: projectsData,
    isLoading,
    error,
    refetch,
  } = useGetIssuerProjectsQuery({
    // 不需要传递userId，后端从JWT token中获取
  });

  // 撤回问卷 mutation
  const [withdrawQuestionnaire, { isLoading: isWithdrawing }] =
    useWithdrawQuestionnaireMutation();

  const [filteredData, setFilteredData] = useState<IssuerQuestionnaire[]>([]);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 筛选状态
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateRange: null as any,
  });

  // 弹框状态
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] =
    useState<IssuerQuestionnaire | null>(null);
  const [fullQuestionnaireData, setFullQuestionnaireData] =
    useState<InitialScreeningQuestionnaire | null>(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | null
  >(null);

  // 使用RTK Query获取问卷详情
  const {
    data: questionnaireDetailData,
    isLoading: loadingDetail,
    error: questionnaireDetailError,
  } = useGetQuestionnaireByIdQuery(selectedQuestionnaireId!, {
    skip: !selectedQuestionnaireId, // 只在有ID时才查询
  });

  // 将后端项目数据转换为问卷列表格式
  const questionnaires: IssuerQuestionnaire[] = React.useMemo(() => {
    if (!projectsData?.data?.projects) return [];

    return projectsData.data.projects.map(project => ({
      id: project.id,
      questionnaireId: project.questionnaire_status?.questionnaire_id, // 问卷ID用于编辑
      projectName: project.name,
      status:
        project.questionnaire_status?.status &&
        isValidQuestionnaireStatus(project.questionnaire_status.status)
          ? project.questionnaire_status.status
          : 'draft',
      currentVersion: 1, // 暂时固定为1，后续可以从数据库获取
      totalVersions: 1,
      aiScore: project.questionnaire_status?.ai_score,
      riskLevel: project.questionnaire_status?.risk_level as
        | 'low'
        | 'medium'
        | 'high',
      submittedAt:
        project.questionnaire_status?.submitted_at || project.created_at,
      reviewedAt: project.questionnaire_status?.reviewed_at,
      reviewedBy: project.questionnaire_status?.reviewed_by,
      lastActivity: project.updated_at,
      rejectedCount:
        project.questionnaire_status?.status === 'rejected' ? 1 : 0,
      approvedCount:
        project.questionnaire_status?.status === 'approved' ? 1 : 0,
      canEdit: project.status === 'pending' || !project.questionnaire_status,
      canResubmit: project.questionnaire_status?.status === 'rejected',
      canDelete:
        project.stage === 'questionnaire' && !project.questionnaire_status,
      // 只有 submitted 状态可以撤回（审核开始前）
      canWithdraw: project.questionnaire_status?.status === 'submitted',
    }));
  }, [projectsData]);

  // 页面配置
  const title = t('questionnaire:issuerList.title');
  const subtitle = t('questionnaire:issuerList.subtitle');
  // Bug修复: 使用/admin路径而不是/，避免重定向问题
  const breadcrumbs = [
    { label: t('questionnaire:issuerList.breadcrumb.home'), path: '/admin' },
    { label: t('questionnaire:issuerList.breadcrumb.current') },
  ];

  // 使用统一状态标签组件，不再需要自定义函数

  // 处理RTK Query的问卷详情数据
  React.useEffect(() => {
    if (
      questionnaireDetailData?.success &&
      questionnaireDetailData.data?.questionnaire
    ) {
      console.log('问卷详情API响应:', questionnaireDetailData);
      setFullQuestionnaireData(questionnaireDetailData.data.questionnaire);
    } else if (questionnaireDetailError) {
      console.error('获取问卷详情失败:', questionnaireDetailError);
      message.error(t('questionnaire:issuerList.message.loadDetailFailed'));
    }
  }, [questionnaireDetailData, questionnaireDetailError, t]);

  // 处理操作
  const handleAction = (action: string, record: IssuerQuestionnaire) => {
    // 优先使用问卷ID，如果没有则使用项目ID
    const targetQuestionnaireId = record.questionnaireId || record.id;

    switch (action) {
      case 'view':
        // 使用弹框显示问卷详情
        console.log('查看问卷详情:', targetQuestionnaireId);
        setSelectedQuestionnaire(record);
        setSelectedQuestionnaireId(targetQuestionnaireId); // 设置ID触发RTK Query
        setDetailModalVisible(true);
        break;
      case 'edit':
        if (!record.questionnaireId) {
          message.warning(
            t('questionnaire:issuerList.message.noQuestionnaireData')
          );
          navigate('/questionnaire', {
            state: {
              from: '/admin/issuer/questionnaires',
              returnTo: 'questionnaire-list',
              projectId: record.id,
              projectName: record.projectName,
            },
          });
          return;
        }
        navigate('/questionnaire', {
          state: {
            from: '/admin/issuer/questionnaires',
            returnTo: 'questionnaire-list',
            editMode: true,
            questionnaireId: record.questionnaireId,
          },
        });
        break;
      case 'resubmit':
        if (!record.questionnaireId) {
          message.error(
            t('questionnaire:issuerList.message.questionnaireIdMissingForResubmit')
          );
          return;
        }
        // 跳转到编辑页面，显示审核意见
        navigate('/questionnaire', {
          state: {
            from: '/admin/issuer/questionnaires',
            returnTo: 'questionnaire-list',
            editMode: true,
            resubmitMode: true, // 标记为重新提交模式
            questionnaireId: record.questionnaireId,
            projectName: record.projectName,
          },
        });
        break;
      case 'withdraw':
        if (!record.questionnaireId) {
          message.error(
            t('questionnaire:issuerList.message.questionnaireIdMissingForWithdraw')
          );
          return;
        }
        confirm.show({
          title: t('questionnaire:issuerList.confirm.withdrawTitle'),
          content: (
            <div>
              <p>
                {t('questionnaire:issuerList.confirm.withdrawContent', {
                  projectName: record.projectName,
                })}
              </p>
              <p style={{ color: '#faad14', fontSize: '12px' }}>
                {t('questionnaire:issuerList.confirm.withdrawHint')}
              </p>
            </div>
          ),
          okText: t('questionnaire:issuerList.confirm.withdrawOk'),
          type: 'warning',
          onConfirm: async () => {
            try {
              await withdrawQuestionnaire({
                id: record.questionnaireId!,
                reason: t('questionnaire:issuerList.message.withdrawReason'),
              }).unwrap();
              message.success(t('questionnaire:issuerList.message.withdrawSuccess'));
              refetch(); // 刷新列表
            } catch (error: any) {
              message.error(
                error.data?.error?.message ||
                  t('questionnaire:issuerList.message.withdrawFailed')
              );
            }
          },
        });
        break;
      case 'delete':
        confirm.show({
          title: t('questionnaire:issuerList.confirm.deleteTitle'),
          content: (
            <div>
              <p>{t('questionnaire:issuerList.confirm.deleteContent')}</p>
              <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
                {t('questionnaire:issuerList.confirm.deleteHint')}
              </p>
            </div>
          ),
          okText: t('common:action.confirm'),
          type: 'error',
          onConfirm: () => {
            // TODO: 实现删除功能
            message.info(t('project:message.featureComingSoon'));
          },
        });
        break;
      default:
        break;
    }
  };

  // 操作菜单
  const getActionMenu = (record: IssuerQuestionnaire) => {
    const items = [];

    items.push({
      key: 'view',
      label: t('questionnaire:issuerList.actions.viewDetail'),
      icon: <EyeOutlined />,
    });

    if (record.canEdit) {
      items.push({
        key: 'edit',
        label: t('questionnaire:issuerList.actions.editQuestionnaire'),
        icon: <EditOutlined />,
      });
    }

    if (record.canResubmit) {
      items.push({
        key: 'resubmit',
        label: t('questionnaire:issuerList.actions.resubmit'),
        icon: <SendOutlined />,
      });
    }

    // Bug #20: 暂时隐藏未实现的删除功能
    // if (record.canDelete) {
    //   items.push({
    //     type: 'divider' as const,
    //   });
    //   items.push({
    //     key: 'delete',
    //     label: '删除草稿',
    //     icon: <DeleteOutlined />,
    //     danger: true,
    //   });
    // }

    return {
      items,
      onClick: ({ key }: { key: string }) => handleAction(key, record),
    };
  };

  // 表格列定义 - 恢复原Table但保持统一状态标签
  const columns: ColumnsType<IssuerQuestionnaire> = [
    {
      title: t('questionnaire:issuerList.columns.projectName'),
      dataIndex: 'projectName',
      key: 'projectName',
      width: 250,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '500', marginBottom: '4px' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Tooltip title={getFullProjectIdTooltip(record.id)}>
              <span style={{ cursor: 'help' }}>
                {t('questionnaire:issuerList.columns.idPrefix')}{' '}
                {formatProjectId(record.id)}
              </span>
            </Tooltip>
            {record.totalVersions > 1 && (
              <span style={{ marginLeft: '8px' }}>
                {t('questionnaire:issuerList.columns.version', {
                  current: record.currentVersion,
                  total: record.totalVersions,
                })}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      title: t('questionnaire:issuerList.columns.reviewStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: status => (
        <UnifiedStatusTag
          type="questionnaire"
          status={status as any}
          size="middle"
        />
      ),
      filters: [
        { text: t('common:statusTag.questionnaire.draft'), value: 'draft' },
        {
          text: t('common:statusTag.questionnaire.submitted'),
          value: 'submitted',
        },
        {
          text: t('common:statusTag.questionnaire.under_review'),
          value: 'under_review',
        },
        {
          text: t('common:statusTag.questionnaire.approved'),
          value: 'approved',
        },
        {
          text: t('common:statusTag.questionnaire.rejected'),
          value: 'rejected',
        },
      ],
    },
    {
      title: t('questionnaire:issuerList.columns.aiScore'),
      dataIndex: 'aiScore',
      key: 'aiScore',
      width: 100,
      render: score => {
        if (!score) return '-';
        const color =
          score >= 90 ? '#52c41a' : score >= 80 ? '#faad14' : '#ff4d4f';
        return (
          <div style={{ color, fontWeight: '500' }}>
            {t('questionnaire:issuerList.columns.aiScoreValue', { score })}
          </div>
        );
      },
      sorter: (a, b) => (a.aiScore || 0) - (b.aiScore || 0),
    },
    {
      title: t('questionnaire:issuerList.columns.riskLevel'),
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      render: riskLevel =>
        riskLevel ? (
          <UnifiedStatusTag
            type="riskLevel"
            status={riskLevel as any}
            size="middle"
          />
        ) : (
          '-'
        ),
    },
    {
      title: t('questionnaire:issuerList.columns.reviewSummary'),
      key: 'reviewStatus',
      width: 120,
      render: (_, record) => (
        <div>
          {record.approvedCount > 0 && (
            <div style={{ color: '#52c41a', fontSize: '12px' }}>
              {t('questionnaire:issuerList.columns.approvedCount', {
                count: record.approvedCount,
              })}
            </div>
          )}
          {record.rejectedCount > 0 && (
            <div style={{ color: '#ff4d4f', fontSize: '12px' }}>
              {t('questionnaire:issuerList.columns.rejectedCount', {
                count: record.rejectedCount,
              })}
            </div>
          )}
          {record.approvedCount === 0 && record.rejectedCount === 0 && (
            <div style={{ color: '#666', fontSize: '12px' }}>
              {t('questionnaire:issuerList.columns.firstSubmission')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('questionnaire:issuerList.columns.reviewer'),
      dataIndex: 'reviewedBy',
      key: 'reviewedBy',
      width: 120,
      render: reviewedBy => reviewedBy || '-',
    },
    {
      title: t('questionnaire:issuerList.columns.lastActivity'),
      dataIndex: 'lastActivity',
      key: 'lastActivity',
      width: 120,
      render: date =>
        new Date(date).toLocaleDateString(
          i18n.language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US'
        ),
      sorter: (a, b) =>
        new Date(a.lastActivity).getTime() - new Date(b.lastActivity).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('questionnaire:issuerList.columns.actions'),
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t('questionnaire:issuerList.actions.viewDetail')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleAction('view', record)}
              size="small"
            />
          </Tooltip>

          {record.canEdit && (
            <Tooltip
              title={t('questionnaire:issuerList.actions.editQuestionnaire')}
            >
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleAction('edit', record)}
                size="small"
              />
            </Tooltip>
          )}

          {record.canResubmit && (
            <Tooltip title={t('questionnaire:issuerList.actions.resubmit')}>
              <Button
                type="text"
                icon={<SendOutlined />}
                onClick={() => handleAction('resubmit', record)}
                size="small"
                style={{ color: '#faad14' }}
              />
            </Tooltip>
          )}

          {record.canWithdraw && (
            <Tooltip title={t('questionnaire:issuerList.actions.withdraw')}>
              <Button
                type="text"
                icon={<RollbackOutlined />}
                onClick={() => handleAction('withdraw', record)}
                size="small"
                loading={isWithdrawing}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}

          {record.canDelete && (
            <Tooltip title={t('questionnaire:issuerList.actions.deleteDraft')}>
              <Button
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => handleAction('delete', record)}
                size="small"
                danger
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // 统计数据
  const statistics = {
    total: questionnaires.length,
    draft: questionnaires.filter(q => q.status === 'draft').length,
    submitted: questionnaires.filter(q => q.status === 'submitted').length,
    under_review: questionnaires.filter(q => q.status === 'under_review')
      .length,
    approved: questionnaires.filter(q => q.status === 'approved').length,
    rejected: questionnaires.filter(q => q.status === 'rejected').length,
  };

  // 检查是否有进行中的问卷（draft, submitted, under_review, need_supplement）
  const activeStatuses = [
    'draft',
    'submitted',
    'under_review',
    'need_supplement',
  ];
  const activeQuestionnaire = questionnaires.find(q =>
    activeStatuses.includes(q.status)
  );
  const hasActiveQuestionnaire = !!activeQuestionnaire;

  // 创建问卷按钮点击处理
  const handleCreateQuestionnaire = () => {
    if (hasActiveQuestionnaire) {
      confirm.show({
        title: t('questionnaire:issuerList.confirm.createBlockedTitle'),
        content: (
          <div>
            <p>
              {t('questionnaire:issuerList.confirm.createBlockedContent', {
                status: t(
                  `common:statusTag.questionnaire.${activeQuestionnaire.status}`
                ),
              })}
            </p>
            <p style={{ color: '#1890ff', fontWeight: 500, margin: '8px 0' }}>
              {activeQuestionnaire.projectName ||
                t('questionnaire:issuerList.confirm.unnamedProject')}
            </p>
            <p style={{ color: '#666', fontSize: '12px' }}>
              {t('questionnaire:issuerList.confirm.createBlockedHint')}
            </p>
          </div>
        ),
        okText: t('questionnaire:issuerList.confirm.createBlockedOk'),
        cancelText: t('common:action.cancel'),
        type: 'warning',
        onConfirm: () => {
          if (activeQuestionnaire.questionnaireId) {
            navigate('/questionnaire', {
              state: {
                editMode: true,
                questionnaireId: activeQuestionnaire.questionnaireId,
                from: '/admin/issuer/questionnaires',
              },
            });
          }
        },
      });
      return;
    }

    navigate('/questionnaire', {
      state: {
        from: '/admin/issuer/questionnaires',
        returnTo: 'questionnaire-list',
      },
    });
  };

  // 筛选处理
  const handleFiltersChange = () => {
    let filtered = [...questionnaires];

    if (filters.status) {
      filtered = filtered.filter(q => q.status === filters.status);
    }

    if (filters.search) {
      filtered = filtered.filter(
        q =>
          q.projectName.toLowerCase().includes(filters.search.toLowerCase()) ||
          q.id.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // 日期范围筛选
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [startDate, endDate] = filters.dateRange;
      filtered = filtered.filter(q => {
        const activityDate = new Date(q.lastActivity);
        return (
          activityDate >= startDate.toDate() && activityDate <= endDate.toDate()
        );
      });
    }

    setFilteredData(filtered);
  };

  React.useEffect(() => {
    handleFiltersChange();
  }, [filters, questionnaires]);

  React.useEffect(() => {
    // 筛选条件变化后重置到第一页，避免出现空页
    setTablePagination(prev => ({ ...prev, current: 1 }));
  }, [filters, questionnaires]);

  // 加载状态
  if (isLoading) {
    return (
      <StandardPageLayout
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
      >
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            {t('questionnaire:issuerList.state.loading')}
          </div>
        </div>
      </StandardPageLayout>
    );
  }

  // 错误状态
  if (error) {
    return (
      <StandardPageLayout
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
      >
        <div style={{ padding: '24px' }}>
          <Empty
            description={t('questionnaire:issuerList.state.loadFailed')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => refetch()}>
              {t('common:action.retry')}
            </Button>
          </Empty>
        </div>
      </StandardPageLayout>
    );
  }

  return (
    <StandardPageLayout
      title={title}
      subtitle={subtitle}
      breadcrumbs={breadcrumbs}
    >
      <div style={{ padding: '24px' }}>
        {/* 统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <Statistic
                title={t('questionnaire:issuerList.stats.total')}
                value={statistics.total}
                valueStyle={{ color: '#1890ff' }}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <Statistic
                title={t('questionnaire:issuerList.stats.draft')}
                value={statistics.draft}
                valueStyle={{ color: '#666' }}
                prefix={<EditOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <Statistic
                title={t('questionnaire:issuerList.stats.underReview')}
                value={statistics.under_review}
                valueStyle={{ color: '#faad14' }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <Statistic
                title={t('questionnaire:issuerList.stats.approved')}
                value={statistics.approved}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <Statistic
                title={t('questionnaire:issuerList.stats.rejected')}
                value={statistics.rejected}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Card>
              <div style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#666',
                    marginBottom: '8px',
                  }}
                >
                  {t('questionnaire:issuerList.stats.approvalRate')}
                </div>
                <Progress
                  type="circle"
                  size={60}
                  percent={Math.round(
                    (statistics.approved /
                      (statistics.approved + statistics.rejected) || 0) * 100
                  )}
                  format={percent => `${percent}%`}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* 筛选和操作区域 */}
        <Card style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder={t('questionnaire:issuerList.filters.search')}
                value={filters.search}
                onChange={e =>
                  setFilters({ ...filters, search: e.target.value })
                }
                style={{ width: '100%' }}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder={t('questionnaire:issuerList.filters.status')}
                value={filters.status}
                onChange={value => setFilters({ ...filters, status: value })}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value="draft">
                  {t('common:statusTag.questionnaire.draft')}
                </Option>
                <Option value="submitted">
                  {t('common:statusTag.questionnaire.submitted')}
                </Option>
                <Option value="under_review">
                  {t('common:statusTag.questionnaire.under_review')}
                </Option>
                <Option value="approved">
                  {t('common:statusTag.questionnaire.approved')}
                </Option>
                <Option value="rejected">
                  {t('common:statusTag.questionnaire.rejected')}
                </Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                placeholder={[
                  t('questionnaire:issuerList.filters.startDate'),
                  t('questionnaire:issuerList.filters.endDate'),
                ]}
                style={{ width: '100%' }}
                onChange={dates => setFilters({ ...filters, dateRange: dates })}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateQuestionnaire}
                style={{ width: '100%' }}
              >
                {t('questionnaire:issuerList.actions.createQuestionnaire')}
              </Button>
            </Col>
          </Row>
        </Card>

        {/* 问卷列表 - 统一glassmorphism风格 */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(16, 185, 129, 0.1)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
            overflow: 'hidden',
          }}
        >
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: tablePagination.current,
              total: filteredData.length,
              pageSize: tablePagination.pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              onChange: (page, pageSize) =>
                setTablePagination(prev => ({
                  current: page,
                  pageSize: pageSize || prev.pageSize,
                })),
              onShowSizeChange: (_, size) =>
                setTablePagination({
                  current: 1,
                  pageSize: size,
                }),
              showTotal: (total, range) =>
                t('questionnaire:issuerList.pagination.totalRange', {
                  total,
                  start: range[0],
                  end: range[1],
                }),
              pageSizeOptions: ['10', '20', '50', '100'],
              style: {
                padding: '16px 24px',
                background:
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.02) 0%, rgba(5, 150, 105, 0.01) 100%)',
                borderTop: '1px solid rgba(16, 185, 129, 0.1)',
              },
            }}
            locale={{
              emptyText: (
                <Empty
                  description={t('questionnaire:issuerList.state.empty')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateQuestionnaire}
                  >
                    {t('questionnaire:issuerList.actions.createFirstQuestionnaire')}
                  </Button>
                </Empty>
              ),
            }}
            scroll={{ x: 1200 }}
            size="small"
            className="emerald-table"
            style={{
              background: 'transparent',
            }}
          />
        </div>
      </div>

      {/* 问卷详情弹框 */}
      {selectedQuestionnaire && (
        <QuestionnaireDetailModal
          visible={detailModalVisible}
          loading={loadingDetail} // 传递加载状态给Modal
          questionnaire={
            fullQuestionnaireData || {
              _id: selectedQuestionnaire.id,
              projectName: selectedQuestionnaire.projectName,
              status: selectedQuestionnaire.status as any,
              aiScore: selectedQuestionnaire.aiScore,
              riskLevel: selectedQuestionnaire.riskLevel,
              submittedAt: selectedQuestionnaire.submittedAt,
              reviewedAt: selectedQuestionnaire.reviewedAt,
              reviewerId: selectedQuestionnaire.reviewedBy,
            }
          }
          onClose={() => {
            setDetailModalVisible(false);
            setSelectedQuestionnaire(null);
            setSelectedQuestionnaireId(null); // 清理ID停止查询
            setFullQuestionnaireData(null);
          }}
          onReview={undefined} // 发行人没有审核权限
        />
      )}
    </StandardPageLayout>
  );
};

export default IssuerQuestionnaireList;
